import { trpc } from '@/lib/trpc';
import { Button } from '@televet/kibble-ui/Button';
import { TextInput } from '@televet/kibble-ui/TextInput';
import { Textarea } from '@televet/kibble-ui/Textarea';
import { Card } from '@televet/kibble-ui/Card';
import { Flex, VStack, Box } from '@televet/kibble-ui/chakra';
import { Select } from '@televet/kibble-ui/Select';
import { useMutation } from '@tanstack/react-query';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import {
  useForm,
  type FieldErrors,
  type UseFormRegister,
  type UseFormHandleSubmit,
  type UseFormWatch,
  type UseFormSetValue,
} from 'react-hook-form';
import { toaster } from '@/components/ui/toaster';
import { parseTags } from './utils';

export const Route = createFileRoute('/_authenticated/agent-context-docs/add')({
  component: AddAgentContextDoc,
  beforeLoad: () => ({
    pageTitle: 'Add Context Document',
  }),
});

const PROVIDER_VALUES = ['Notion'] as const;
type ProviderValue = (typeof PROVIDER_VALUES)[number];

export interface AgentContextDocFormData {
  provider: ProviderValue;
  externalId: string;
  title: string;
  description: string;
  tags: string;
}

const PROVIDERS = PROVIDER_VALUES.map((v) => ({ value: v, label: v }));

export function AgentContextDocForm({
  onSubmit,
  onCancel,
  errors,
  register,
  handleSubmit,
  watch,
  setValue,
}: {
  onSubmit: (data: AgentContextDocFormData) => void;
  onCancel: () => void;
  errors: FieldErrors<AgentContextDocFormData>;
  register: UseFormRegister<AgentContextDocFormData>;
  handleSubmit: UseFormHandleSubmit<AgentContextDocFormData>;
  watch: UseFormWatch<AgentContextDocFormData>;
  setValue: UseFormSetValue<AgentContextDocFormData>;
}) {
  return (
    <Flex flex="1 1 auto" direction="column" align="stretch" h="100%">
      <Flex flex="1 1 auto" justify="center" align="flex-start" pt={8}>
        <Card
          flex="1 1 auto"
          direction="column"
          borderColor="border.default"
          maxW="3xl"
        >
          <Box p={10}>
            <form onSubmit={handleSubmit(onSubmit)}>
              <VStack gap={6} align="stretch">
                <TextInput
                  label="Title"
                  required
                  invalid={!!errors.title}
                  errorText={errors.title?.message}
                  placeholder="e.g. Patient Retention Health - Definitions & Benchmarks"
                  autoFocus
                  {...register('title', { required: 'Title is required' })}
                />

                <Select
                  label="Provider"
                  required
                  invalid={!!errors.provider}
                  errorText={errors.provider?.message}
                  options={PROVIDERS}
                  value={[watch('provider')]}
                  onValueChange={(details) =>
                    setValue('provider', details.value[0] as ProviderValue)
                  }
                />

                <TextInput
                  label="External ID"
                  required
                  invalid={!!errors.externalId}
                  errorText={errors.externalId?.message}
                  helperText="The provider-specific identifier (e.g. Notion page UUID). The page must be shared with the integration."
                  placeholder="e.g. 1a2b3c4d-5e6f-7a8b-9c0d-1e2f3a4b5c6d"
                  {...register('externalId', {
                    required: 'External ID is required',
                  })}
                />

                <Textarea
                  label="Description"
                  required
                  invalid={!!errors.description}
                  errorText={errors.description?.message}
                  helperText="The agent reads this to decide whether to fetch the full document content. Be specific about what knowledge it contains."
                  placeholder="A short summary of what this document covers, so the agent can judge whether it's relevant to the current question."
                  rows={3}
                  {...register('description', {
                    required: 'Description is required',
                  })}
                />

                <TextInput
                  label="Tags"
                  invalid={!!errors.tags}
                  errorText={errors.tags?.message}
                  helperText="Comma-separated tags for categorization and filtering."
                  placeholder="e.g. benchmarks, definitions, patient-retention"
                  {...register('tags')}
                />

                <Flex justify="flex-end">
                  <Flex gap={2}>
                    <Button variant="secondary" onClick={onCancel}>
                      Cancel
                    </Button>
                    <Button type="submit">Save</Button>
                  </Flex>
                </Flex>
              </VStack>
            </form>
          </Box>
        </Card>
      </Flex>
    </Flex>
  );
}

function AddAgentContextDoc() {
  const navigate = useNavigate();

  const { mutate } = useMutation(
    trpc.agentContextDoc.createOne.mutationOptions({
      onSuccess: (data) => {
        toaster.success({
          title: 'Document created',
          description: data.message,
        });
        navigate({ to: '/agent-context-docs' });
      },
      onError: (error) => {
        toaster.error({
          title: 'Failed to create document',
          description: error.message || 'Failed to create document',
          duration: 10000,
          closable: true,
        });
      },
    })
  );

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    formState: { errors },
  } = useForm<AgentContextDocFormData>({
    defaultValues: { provider: 'Notion' },
  });

  const onSubmit = (data: AgentContextDocFormData) => {
    mutate({
      provider: data.provider,
      externalId: data.externalId,
      title: data.title,
      description: data.description,
      tags: parseTags(data.tags),
    });
  };

  return (
    <AgentContextDocForm
      onSubmit={onSubmit}
      onCancel={() => navigate({ to: '/agent-context-docs' })}
      errors={errors}
      register={register}
      handleSubmit={handleSubmit}
      watch={watch}
      setValue={setValue}
    />
  );
}
